import * as countries from 'smg/countries';
import * as shippingoptions from 'smg/customer-api/shippingoptions';
import * as users from 'smg/customer-api/users';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { Phone } from 'smg/phone';
import { MaybePromise } from 'smg/promise';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as address from './address';
import * as cards from './cards';

export const Get = shippingoptions.GetShippingOptions;
export const New = shippingoptions.NewShippingOption;

export async function PromptNew(user: users.UserDetails, shippingOption?: models.ShippingOption): Promise<models.ShippingOption> {
    const body = template.Clone('#shipping-option-modal-form-template');

    const fullName = body.querySelector('#modal-full-name') as HTMLInputElement;
    const company = body.querySelector('#modal-company') as HTMLInputElement;
    const phone = body.querySelector('#modal-phone') as HTMLInputElement;
    const email = body.querySelector('#modal-email') as HTMLInputElement;
    const addressLine1 = body.querySelector('#modal-address-line-1') as HTMLInputElement;
    const addressLine2 = body.querySelector('#modal-address-line-2') as HTMLInputElement;
    const city = body.querySelector('#modal-city') as HTMLInputElement;
    const state = body.querySelector('#modal-state') as HTMLSelectElement;
    const zip = body.querySelector('#modal-zip') as HTMLInputElement;
    const country = body.querySelector('#modal-country') as HTMLSelectElement;

    if (shippingOption) {
        phone.value = shippingOption.phone || '';
    }
    const p = new Phone(phone);
    await countries.Selects(country, state, {
        defaultUnitedStates: true,
        stateParentSelector: 'div',
        phone: p,
        onStateHide: () => zip.parentElement?.classList.replace('col-md-2', 'col-md-6'),
        onStateShow: () => zip.parentElement?.classList.replace('col-md-6', 'col-md-2'),
    });
    if (shippingOption) {
        fullName.value = shippingOption.fullName || '';
        company.value = shippingOption.company || '';
        email.value = shippingOption.email || '';
        addressLine1.value = shippingOption.addressLine1 || '';
        addressLine2.value = shippingOption.addressLine2 || '';
        city.value = shippingOption.city || '';
        zip.value = shippingOption.zip || '';
        country.value = String(shippingOption.countryID || '');
        country.dispatchEvent(new Event('change'));
        state.value = String(shippingOption.stateID || '');
    }

    CheckPOBoxNewAddress(country, state, addressLine1, addressLine1);

    return new Promise(resolve => {
        new Modal({
            title: shippingOption ? 'Edit Shipping Address' : 'New Shipping Address',
            outsideClosable: false,
            size: 'modal-lg',
            body,
            onSubmit: async () => shippingoptions.NewShippingOption(user, {
                fullName: fullName.value,
                company: company.value,
                addressLine1: addressLine1.value,
                addressLine2: addressLine2.value,
                city: city.value,
                state: state.value ? {
                    id: parseInt(state.value, 10) as models.StateID,
                    stateCode: state.getAttribute('data-state-code') || undefined,
                    name: state.getAttribute('data-name') || undefined,
                } : undefined,
                zip: zip.value,
                country: {
                    id: parseInt(country.value, 10) as models.CountryID,
                    countryCode2: country.getAttribute('data-country-code-2') || undefined,
                    countryCode3: country.getAttribute('data-country-code-3') || undefined,
                    name: country.getAttribute('data-name') || undefined,
                },
                phone: await p.Format(),
                email: email.value,
            }).then(async newShippingOption => {
                if (shippingOption?.id) {
                    await shippingoptions.DeleteShippingOption(shippingOption.id);
                }
                toast.Success('Your shipping address has been added to your account.');
                resolve(newShippingOption);
            }),
        });
    });
}

export async function Draw(shippingOption: models.ShippingOption, el?: HTMLElement): Promise<HTMLElement> {
    const country = await countries.GetCountry(shippingOption.countryID || 0);
    const state = await countries.GetState(shippingOption.countryID || 0, shippingOption.stateID || 0);

    return address.Draw({
        fullName: shippingOption.fullName || '',
        company: shippingOption.company || '',
        addressLine1: shippingOption.addressLine1 || '',
        addressLine2: shippingOption.addressLine2 || '',
        city: shippingOption.city || '',
        state: state ? {
            name: state.name,
            stateCode: state.stateCode,
        } : undefined,
        zip: shippingOption.zip || '',
        country: country ? {
            name: country.name,
            countryCode2: country.countryCode2,
            countryCode3: country.countryCode3,
        } : undefined,
    }, shippingOption.phone, shippingOption.email, el);
}

export async function Prompt(user: users.UserDetails): Promise<models.ShippingOption> {
    const shippingOptions = await shippingoptions.GetShippingOptions({
        'data': 'fullName,company,addressLine1,addressLine1,city,stateID,zip,countryID,phone,email',
        'sort': 'created desc',
        'q': 'has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip',
    });
    if (!shippingOptions.length) {
        return PromptNew(user);
    }

    let resolve: (shippingOption: MaybePromise<models.ShippingOption>) => void;
    const p = new Promise<models.ShippingOption>(r => resolve = r);

    const body = document.createDocumentFragment();

    for (const shippingOption of shippingOptions) {
        const h = cards.DrawUse(await Draw(shippingOption), 'Use this shipping address');
        (h.querySelector('.use') as HTMLElement).addEventListener('click', e => {
            e.preventDefault();

            resolve(shippingOption);
            m.Close();
        });
        body.appendChild(h);
    }

    const m = new Modal({
        title: 'Select Shipping Address',
        confirmText: 'Create New Shipping Address',
        body,
        onSubmit: () => resolve(PromptNew(user)),
    });

    return p;
}

const poBoxPattern = /^ *((#\d+)|((box|bin)[-. /\\]?\d+)|(.*p[ .]? ?(o|0)[-. /\\]? *-?((box|bin)|b|(#|n|num|number)?\d+))|(p(ost|ostal)? *(o(ff(ice)?)?)? *((box|bin)|b)? *(#|n|num|number)*\d+)|(p *-?\/?(o)? *-?box)|post office box|((box|bin)|b) *(#|n|num|number)? *\d+|(#|n|num|number) *\d+)/i;
const $warning = document.querySelector('#po-warning-container') as HTMLElement;

export function CheckPOBoxNewAddress($country: HTMLSelectElement, $state: HTMLSelectElement, $addressLine1: HTMLInputElement, $target: HTMLElement) {
    const togglePOBoxWarning = async () => {
        $warning.classList.add('d-none');
        const countryID = parseInt($country.value, 10);
        if (countryID !== models.CountryID.COUNTRIES_UNITED_STATES) {
            return;
        }
        if (!poBoxPattern.test($addressLine1.value)) {
            return;
        }
        const stateID = parseInt($state.value, 10);
        const state = await countries.GetState(countryID, stateID);
        if (state?.apo) {
            return;
        }

        if ($target.id === 'shipping-form') {
            const appendTo = $target.querySelector('#shipping-form-po-alert-container') as HTMLInputElement;
            appendTo.appendChild($warning);
        }

        if ($target.id === 'modal-address-line-1') {
            $target.parentElement?.parentElement?.insertBefore($warning, $target.parentElement.nextElementSibling);
        }

        $warning.classList.remove('d-none');
    };
    $country.addEventListener('change', togglePOBoxWarning);
    $state.addEventListener('change', togglePOBoxWarning);
    $addressLine1.addEventListener('input', togglePOBoxWarning);
}

export async function CheckPOBoxAddress(country: models.CountryID, state: number, addressLine: string, $target: HTMLElement) {
    if (country !== models.CountryID.COUNTRIES_UNITED_STATES) {
        if ($target.lastElementChild?.id === 'po-warning-container') {
            $target.lastElementChild.remove();
        }
        return;
    }

    if (!poBoxPattern.test(addressLine)) {
        if ($target.lastElementChild?.id === 'po-warning-container') {
            $target.lastElementChild.remove();
        }
        return;
    }

    const getState = await countries.GetState(country, state);
    if (getState?.apo) {
        if ($target.lastElementChild?.id === 'po-warning-container') {
            $target.lastElementChild.remove();
        }
        return;
    }

    if ($target.lastElementChild?.id !== 'po-warning-container') {
        $warning.classList.remove('d-none');
        $warning.classList.add('mt-2');
        const $warningClone = $warning.cloneNode(true);
        $warning.classList.add('d-none');
        const $addressSpan = $target.querySelector('.address-lines') as HTMLElement;
        $addressSpan.parentElement?.append($warningClone);
    }
}
